import { ApolloError, ApolloLink, Observable as ApolloObservable } from '@apollo/client/core';
import { concat } from 'rxjs';
import { debounceTime, first, skip } from 'rxjs/operators';
import type { FetchResult, Operation } from '@apollo/client/core';
import type { Options } from 'apollo-angular/http';
import type { ExecutionResult, GraphQLError } from 'graphql';
import type { Observable } from 'rxjs';

const apolloProperties = ['__typename', 'Symbol(id)'];

export function apolloClean(data, removeApolloProperties = true) {
  if (Array.isArray(data)) {
    return data.map(value => apolloClean(value, removeApolloProperties));
  } else if (data !== null && typeof data === 'object') {
    return Object.keys(data).reduce((dataCopy, key) => {
      if (removeApolloProperties === false || !apolloProperties.includes(key)) {
        dataCopy[key] = apolloClean(data[key], removeApolloProperties);
      }
      return dataCopy;
    }, {});
  }

  return data;
}

export function debounceAfterFirst(observable: Observable<any>, debounce: number) {
  return concat(observable.pipe(first()), observable.pipe(skip(1), debounceTime(debounce)));
}

export interface GraphQLInput<T> {
  input: T;
}


/**
 * Following part is fork of apollo-link-error repository as there were some customizations needed
 * TODO: check later if official repo is working as expected
 */
export interface ErrorResponse {
  graphQLErrors?: GraphQLError[];
  networkError?: Error;
  response?: ExecutionResult;
  operation: Operation;
}

export namespace ErrorLink {
  /**
   * Callback to be triggered when an error occurs within the link stack.
   */
  export interface ErrorHandler {
    (error: ErrorResponse): void;
  }
}

// For backwards compatibility.
export import ErrorHandler = ErrorLink.ErrorHandler;

export const onError = (errorHandler: ErrorHandler): ApolloLink => {
  return new ApolloLink((operation, forward) => {
    return new ApolloObservable(observer => {
      let sub;
      try {
        sub = forward(operation).subscribe({
          next: result => {
            if (result.errors) {
              errorHandler({
                graphQLErrors: result.errors as any, // TODO: Fix typing
                response: result as any, // TODO: Fix typing
                operation,
              });
            }
            observer.next(result);
          },
          error: networkError => {
            errorHandler({
              operation,
              networkError,
              // Network errors can return GraphQL errors on for example a 403
              graphQLErrors: networkError.error && networkError.error.errors
            });
            observer.error(new ApolloError({
              graphQLErrors: networkError.error && networkError.error.errors,
              networkError
            }));
          },
          complete: observer.complete.bind(observer),
        });
      } catch (e) {
        errorHandler({ networkError: e, operation });
        observer.error(e);
      }

      return () => {
        if (sub) {
          sub.unsubscribe();
        }
      };
    });
  });
};

export interface APIErrorContext {
  networkError: Error;
  graphQLErrors: any[];
  response: any;
  operation: Operation;
  errorWhiteList: number[];
}

export function createDefaultAPIErrorLink(options: {
  errorHandlerService?: { handleApiError(context: APIErrorContext): void };
} = {}) {
  return onError(({ networkError, graphQLErrors, response, operation }) => {
    const context = operation.getContext();

    if (graphQLErrors) {
      graphQLErrors.forEach((graphqlError: (GraphQLError & { api_error: any })) => console.error(getFormattedGraphqlError(graphqlError)));
      console.log('[GraphQL Query]', operation.query.loc.source.body);
      console.log('[GraphQL Variables]', '\n' + JSON.stringify(operation.variables));
    }

    if (networkError) {
      console.log(`[Network error]: ${JSON.stringify(networkError)}`);
    }

    const error = context.error;
    const errorWhiteList = context && context.errorWhiteList;
    if (options.errorHandlerService && error !== false) {
      options.errorHandlerService.handleApiError({
        networkError,
        graphQLErrors,
        response,
        operation,
        errorWhiteList
      });
    }
  });
}

export class ErrorLink extends ApolloLink {
  private link: ApolloLink;

  constructor(errorHandler: ErrorLink.ErrorHandler) {
    super();
    this.link = onError(errorHandler);
  }

  public request(operation, forward): ApolloObservable<FetchResult> | null {
    return this.link.request(operation, forward);
  }
}
export function getFormattedGraphqlError(error: (GraphQLError & { api_error: any }), pretty = false) {
  // eslint:disable-next-line: max-line-length
  return `[Pipeliner GraphQL error]: Message: ${error.message}, Location: ${JSON.stringify(error.locations)}, Path: ${JSON.stringify(error.path)}${error.api_error ? `, ApiError: ${JSON.stringify(error.api_error, null, pretty ? 2 : null)}` : ''}`;
}

export interface LinkInfo {
  link: ApolloLink;
  linkOptions: Options;
}
